import { useState, useMemo, useEffect, useId } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Box,
    Typography,
    Autocomplete,
    TextField,
    CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { withAutocompleteFieldShell } from '../autocomplete/autocompleteFieldShell';

export default function Dropdown({
    data = [],
    selectedItem,
    handleChange,
    label,
    size = 'small',
    error,
    helperText,
    handleNew,
    handleNewText,
    variant = 'standard',
    disabled = false,
    sx = {},
    mode = 'dropdown', // 'dropdown' ou 'autocomplete'
    // 'floating' (padrão, comportamento atual/inalterado) ou 'static-micro'
    // — label estático em uppercase acima do select, nunca flutuando dentro
    // dele (DESIGN-SYSTEM.md §5.5 "Static-label select" / `.ffs` no
    // protótipo). Só usado hoje pelo modo não-embedded de Form.js (produtos).
    labelVariant = 'floating',
    loading = false,
    getOptionLabel = (option) => option?.name || option?.Name || '',
    getOptionValue = (option) => option?.id ?? option?.ID ?? option?.Id ?? '',
    filterOptions,
    noOptionsText = 'Nenhuma opção disponível',
    renderOption,
    onInputChange,
    freeSolo = false,
    ...props
}) {
    const theme = useTheme();
    const staticLabelId = useId();
    const [inputValue, setInputValue] = useState('');

    // Encontrar a opção selecionada baseada no selectedItem
    const selectedOption = useMemo(() => {
        if (!selectedItem || mode === 'dropdown') return null;
        return (
            data.find(
                (item) =>
                    String(getOptionValue(item) ?? '') === String(selectedItem ?? '')
            ) || null
        );
    }, [selectedItem, data, getOptionValue, mode]);

    // Autocomplete controla inputValue; ao mudar seleção pelo estado do pai (ex.: cliente recém-criado), o rótulo precisa ser sincronizado
    useEffect(() => {
        if (mode !== 'autocomplete') return;
        if (!selectedItem) {
            setInputValue('');
            return;
        }
        if (selectedOption) {
            setInputValue(getOptionLabel(selectedOption) || '');
        }
        // getOptionLabel deliberadamente omitido: o default seria nova referência a cada render
    }, [mode, selectedItem, selectedOption]);

    // Filtrar opções baseado no input (apenas para autocomplete)
    const filteredOptions = useMemo(() => {
        if (mode === 'dropdown' || !inputValue) return data;
        
        const lowerInput = inputValue.toLowerCase();
        return data.filter(item => {
            const label = getOptionLabel(item).toLowerCase();
            return label.includes(lowerInput);
        });
    }, [data, inputValue, getOptionLabel, mode]);

    // Handler para autocomplete
    const handleAutocompleteChange = (event, newValue) => {
        // Garantir que sempre passamos apenas valores primitivos
        if (newValue && typeof newValue === 'object' && !Array.isArray(newValue)) {
            try {
                const newValueId = getOptionValue(newValue);
                // Garantir que o valor é primitivo (string, number, boolean, null, undefined)
                let value = '';
                if (newValueId !== null && newValueId !== undefined) {
                    // Converter para string se não for já um primitivo
                    if (typeof newValueId === 'string' || typeof newValueId === 'number' || typeof newValueId === 'boolean') {
                        value = String(newValueId);
                    } else {
                        // Se for objeto, tentar extrair um ID ou usar string vazia
                        value = '';
                    }
                }
                handleChange(value);
            } catch (error) {
                console.error('Error in handleAutocompleteChange:', error);
                handleChange('');
            }
        } else {
            handleChange('');
        }
    };

    const handleAutocompleteInputChange = (event, newInputValue, reason) => {
        // Garantir que newInputValue é sempre uma string
        const inputValue = newInputValue || '';
        setInputValue(inputValue);
        
        if (onInputChange) {
            // Passar apenas o valor da string, nunca o evento completo
            // onInputChange deve receber apenas o valor string
            if (typeof onInputChange === 'function') {
                onInputChange(inputValue);
            }
        }
    };

    // Função padrão para renderizar opções no autocomplete
    const defaultRenderOption = (props, option) => (
        <li {...props} key={getOptionValue(option)}>
            {getOptionLabel(option)}
        </li>
    );

    // Modo Autocomplete
    if (mode === 'autocomplete') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <Autocomplete
                    fullWidth
                    value={selectedOption}
                    onChange={handleAutocompleteChange}
                    inputValue={inputValue}
                    onInputChange={handleAutocompleteInputChange}
                    options={filterOptions ? filterOptions(filteredOptions) : filteredOptions}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                        String(getOptionValue(option) ?? '') ===
                        String(getOptionValue(value) ?? '')
                    }
                    disabled={disabled}
                    loading={loading}
                    loadingText="Carregando..."
                    noOptionsText={noOptionsText}
                    freeSolo={freeSolo}
                    renderOption={renderOption || defaultRenderOption}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            label={label}
                            variant={variant}
                            size={size}
                            error={error}
                            helperText={helperText}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                '& .MuiInput-underline:before': {
                                    borderBottomColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255,255,255,0.2)' 
                                        : 'rgba(0,0,0,0.1)',
                                },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                    borderBottomColor: theme.palette.primary.main
                                },
                                '& .MuiInput-underline.Mui-focused:after': {
                                    borderBottomColor: theme.palette.primary.main
                                }
                            }}
                        />
                    )}
                    // `sx` (ex.: `standaloneFieldSx`) vai na raiz do Autocomplete; o helper
                    // só acrescenta o ajuste de padding do input quando `sx` traz a casca 52px.
                    sx={{ width: '100%', ...withAutocompleteFieldShell(sx) }}
                    {...props}
                />
                {handleNew && (
                    <Box 
                        onClick={() => handleNew()} 
                        sx={{ 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center',
                            color: theme.palette.primary.main,
                            '&:hover': {
                                opacity: 0.7
                            }
                        }}
                    >
                        <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
                    </Box>
                )}
            </Box>
        );
    }

    // Array "achatado" (não um Fragment `<>...</>`) — o algoritmo de matching
    // interno do MUI Select usa `React.Children.toArray(children)` e procura
    // `child.props.value === value` em cada item de primeiro nível; um único
    // Fragment como filho vira um único item SEM `value`, e o Select nunca
    // encontra a opção selecionada (mostra o campo em branco mesmo com um
    // valor válido). Um array simples é achatado corretamente.
    const menuItems = [
        ...(handleNew
            ? [
                  <MenuItem key="__dropdown_handle_new" onClick={() => handleNew()} disableScrollLock={true}>
                      <AddCircleOutlineIcon sx={{ mr: 1, fontSize: 18 }} />
                      <span style={{ fontStyle: 'italic' }}>{handleNewText}</span>
                  </MenuItem>,
                  <Divider key="__dropdown_handle_new_divider" />,
              ]
            : []),
        ...data.map((item) => (
            <MenuItem key={item.id} value={item.id}>
                {getOptionLabel(item)}
            </MenuItem>
        )),
    ];

    // Modo Dropdown — variante "static-micro" (DESIGN-SYSTEM.md §5.5 /
    // `.ffs` no protótipo): label estático uppercase SEMPRE acima do select,
    // nunca flutuando dentro dele. O `Select` fica sem `InputLabel`/`label`
    // (sem notch no outline), com o rótulo renderizado como `Typography`
    // separado, associado via `id`/`labelId` por acessibilidade.
    if (labelVariant === 'static-micro') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: 0 }}>
                    <Typography
                        id={staticLabelId}
                        component="label"
                        sx={{
                            fontSize: '10.5px',
                            fontWeight: 800,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '.05em',
                        }}
                    >
                        {label}
                    </Typography>
                    <FormControl fullWidth size={size} error={error} sx={sx}>
                        <Select
                            disabled={disabled}
                            value={selectedItem || ''}
                            labelId={staticLabelId}
                            onChange={(e) => handleChange(e.target.value)}
                            error={error}
                            size={size}
                            variant={variant}
                            sx={{
                                '& .MuiSelect-select': {
                                    color: selectedItem ? theme.palette.text.primary : theme.palette.text.secondary
                                },
                            }}
                            {...props}
                        >
                            {menuItems}
                        </Select>
                        {helperText && <FormHelperText>{helperText}</FormHelperText>}
                    </FormControl>
                </Box>
                {handleNew && (
                    <Box
                        onClick={() => handleNew()}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            height: 52,
                            color: theme.palette.primary.main,
                            '&:hover': {
                                opacity: 0.7
                            }
                        }}
                    >
                        <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
                    </Box>
                )}
            </Box>
        );
    }

    // Modo Dropdown (padrão, label flutuante via InputLabel do MUI)
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            <FormControl fullWidth size={size} sx={sx}>
                <InputLabel
                    sx={{
                        color: selectedItem ? theme.palette.text.primary : theme.palette.text.secondary,
                        '&.Mui-focused': {
                            color: theme.palette.primary.main
                        }
                    }}
                >
                    {label}
                </InputLabel>
                <Select
                    disabled={disabled}
                    value={selectedItem || ''}
                    label={label}
                    onChange={(e) => handleChange(e.target.value)}
                    error={error}
                    size={size}
                    variant={variant}
                    sx={{
                        '& .MuiSelect-select': {
                            color: selectedItem ? theme.palette.text.primary : theme.palette.text.secondary
                        },
                        '& .MuiInput-underline:before': {
                            borderBottomColor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.2)'
                                : 'rgba(0,0,0,0.1)',
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottomColor: theme.palette.primary.main
                        },
                        '& .MuiInput-underline.Mui-focused:after': {
                            borderBottomColor: theme.palette.primary.main
                        }
                    }}
                    {...props}
                >
                    {menuItems}
                </Select>
            </FormControl>
        </Box>
    )
}